import { Position } from '../constants';

// Entity types for the unified entity system
export enum EntityType {
  MINER = 'miner',
  BELT = 'belt',
  ASSEMBLER = 'assembler',
  RABBIT = 'rabbit',
  WORM = 'worm',
}

// Base interface for all serializable entity data
export interface EntityData {
  id: string;
  type: EntityType;
  position: Position;
  [key: string]: unknown; // Allow additional properties for specific entity types
}

// Abstract base class for all entities in the game world
export abstract class Entity {
  public readonly id: string;
  public readonly type: EntityType;
  public position: Position;

  constructor(id: string, type: EntityType, position: Position) {
    this.id = id;
    this.type = type;
    this.position = position;
  }

  // Get the visual character to display for this entity
  abstract getDisplayChar(): string;

  // Get CSS class name for styling
  abstract getCSSClass(): string;

  // Get tooltip text for this entity
  abstract getTooltip(): string;

  // Whether other entities can pass through this entity
  abstract isSolid(): boolean;

  // Update entity state (called each game tick)
  abstract update(deltaTime: number): void;

  // Check if entity can be placed at given position
  abstract canPlace(position: Position, context: unknown): boolean;

  // Serialize entity to data object for saving
  abstract serialize(): EntityData;

  // Create entity from serialized data
  static deserialize(_data: EntityData): Entity {
    throw new Error('Deserialize must be implemented by each entity type');
  }

  // Check if this entity is at the given position
  isAt(position: Position): boolean {
    return this.position.x === position.x && this.position.y === position.y;
  }

  // Move entity to new position
  moveTo(newPosition: Position): void {
    this.position = newPosition;
  }

  // Get unique key for this entity's position
  getPositionKey(): string {
    return `${this.position.x},${this.position.y}`;
  }

  // Check if entity should be removed from the world
  shouldDestroy(): boolean {
    return false;
  }

  // Handle collision with another entity (e.g., player collision)
  // Returns whether the collision was handled/consumed
  collide(_other: Entity): boolean {
    return false; // Default: no collision handling
  }
}
